/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

import Render3DPlugin from './Render3DPlugin'
//
// This class is responsible for loading a ThreeJS model from a .glb file.
// It also ensures that data is reused correctly when loading multiple of
// the same model.
export default new (class ModelLoader {
  promises: any
  currentPromise: null
  loader: GLTFLoader
  dracoLoader: DRACOLoader

  constructor() {
    // Model URL to Promise map
    this.promises = {}

    // Last promise
    this.currentPromise = null

    // DRACOLoader, https://threejs.org/docs/index.html?q=DRACOLoader#examples/en/loaders/DRACOLoader
    // Draco is an open source library for compressing and decompressing 3D meshes and point clouds.
    this.dracoLoader = new DRACOLoader()
    this.dracoLoader.setDecoderConfig({ type: 'js' })
    this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')

    // Start Loader
    this.loader = new GLTFLoader()
    this.loader.setDRACOLoader(this.dracoLoader)
  }

  /**
   * Load the specified model URL
   *
   * @param {string} url The URL to the model to load
   * @returns {Object3D} A ThreeJS object which can be added to a scene
   */
  load(url: string) {
    // Check for existing promise for this model
    if (this.promises[url]) return this.promises[url].then((obj: any) => this.clone(obj))

    // Not found, load it
    const promise = this.loadModel(url)

    // Store and return promise
    this.promises[url] = promise
    return promise.then(obj => this.clone(obj))
  }

  /** @private Load a model and return a ThreeJS object */
  async loadModel(url: string) {
    // Load scene
    const glb: any = await new Promise((resolve, reject) =>
      this.loader.load(url, resolve, undefined, reject)
    )

    // Load skybox texture
    Render3DPlugin.loadSkyboxCubeMap().then((skyboxTexture: any) => {
      // Apply skybox texture as env map to all materials. First traverse each object
      // Adapted from https://github.com/donmccurdy/three-gltf-viewer/blob/98ad8ee8528e87a4654ef7a86cf79042ba1dcea3/src/viewer.js#L394
      glb.scene.traverse((node: any) => {
        // Stop if not a mesh
        if (!node.isMesh) return

        // Get materials array
        const materials = Array.isArray(node.material) ? node.material : [node.material]

        // Go through each material
        materials.forEach((material: any) => {
          // Apply env map if supported material type
          if (material.isMeshStandardMaterial || material.isGLTFSpecularGlossinessMaterial) {
            material.envMap = skyboxTexture
            material.needsUpdate = true
          }
        })
      })
    })

    // Store properties for caching
    glb.scene.fromURL = url
    glb.scene.animations = glb.animations
    return glb.scene
  }

  /** @private Clones a ThreeJS object */
  clone(obj3d: any) {
    // @ts-ignore
    const cloned = THREE.SkeletonUtils.clone(obj3d)
    cloned.fromURL = obj3d.fromURL
    cloned.animations = obj3d.animations
    return cloned
  }
})()
